import React, { useState, useEffect } from 'react';
import { Box, Button, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom'; 

import { VMInformation, VMDocTecnica, VMPlansAndProcedures, VMRecords, VMReview, VMEmergencyPlan } from '../custom';
import MobileMenu from '../custom/MobileMenu';

enum Sections {
  INFORMACOES = 'informacoes',
  TECNICA = 'tecnica',
  PROCEDIMENTOS = 'procedimentos',
  REGISTRO = 'registro',
  REVISAO = 'revisao',
  PLANO = 'plano',
}

type SectionData = {
  key: Sections;
  label: string;
  content: React.ReactNode;
};

interface IVMenuProps {
  id: string | undefined;
}

export const VMenu: React.FC<IVMenuProps> = ({ id }) => {
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation(); 
  const [openSubmenu, setOpenSubmenu] = useState<Sections | null>(null);
  const [activeSection, setActiveSection] = useState<Sections | null>(null);

  if (id == undefined) {
    id = '';
  }

  const getCurrentSectionFromUrl = (pathname: string): Sections | null => {
    if (pathname.includes('/entrepreneu_id') ||
      pathname.includes('/features-undertaking') ||
      pathname.includes('/organization_chart')) {
      return Sections.INFORMACOES;
    }
    if (pathname.includes('/technical_characteristics_project') ||
      pathname.includes('/files/file') ||
      pathname.includes('/files/foto')) {
      return Sections.TECNICA;
    }
    if (pathname.includes('/maintenance_planning') ||
      pathname.includes('/monitoring_instrumentation_plan') ||
      pathname.includes('/dam_safety_inspections_planning')) {
      return Sections.PROCEDIMENTOS;
    }
    if (pathname.includes('/risr') ||
      pathname.includes('/rise') ||
      pathname.includes('/operation_records') ||
      pathname.includes('/instrumentation_geotechnical_records')) {
      return Sections.REGISTRO;
    }
    if (pathname.includes('/rpsb')) {
      return Sections.REVISAO;
    }
    if (pathname.includes('/emergency_plan') ||
      pathname.includes('/semiannual_internal_training') ||
      pathname.includes('/annual_guidance_seminar') ||
      pathname.includes('/emergency_situation_simulation') ||
      pathname.includes('/emergency_situation_register')) {
      return Sections.PLANO;
    }
    return null;
  };

  useEffect(() => {
    const currentSection = getCurrentSectionFromUrl(location.pathname);
    if (currentSection) {
      setActiveSection(currentSection);
      setOpenSubmenu(null); 
    }
  }, [location.pathname]);

  const sectionsData: SectionData[] = [
    {
      key: Sections.INFORMACOES,
      label: 'Informações',
      content: <VMInformation id={id} />,
    },
    {
      key: Sections.TECNICA,
      label: 'DOC. TÉCNICA',
      content: <VMDocTecnica id={id} />,
    },
    {
      key: Sections.PROCEDIMENTOS,
      label: 'Procedimentos',
      content: <VMPlansAndProcedures id={id} />,
    },
    {
      key: Sections.REGISTRO,
      label: 'Registros',
      content: <VMRecords id={id} />,
    },
    {
      key: Sections.REVISAO,
      label: 'Revisão',
      content: <VMReview id={id} />
    },
    {
      key: Sections.PLANO,
      label: 'PAE',
      content: <VMEmergencyPlan id={id} />,
    },
  ];

  const menus = [
    {
      subMenu: {
        title: 'Informações',
        buttons: [
          { name: 'Identificação do empreendedor', route: `/information_dam/${id}/entrepreneu_id` },
          { name: 'Caracterização da barragem', route: `/information_dam/${id}/features-undertaking` },
          { name: 'Organograma geotecnia', route: `/information_dam/${id}/organization_chart` },
        ]
      }
    },
    {
      subMenu: {
        title: 'Doc. Técnica',
        buttons: [
          { name: 'Características técnicas', route: `/information_dam/${id}/technical_characteristics_project` },
          { name: 'Arquivos da Barragem', route: `/information_dam/${id}/files/file` },
          // { name: 'Registros fotográficos', route: `/information_dam/${id}/files/foto` },
        ]
      }
    },
    {
      subMenu: {
        title: 'Procedimentos',
        buttons: [
          { name: 'Planejamento das manutenções', route: `/information_dam/${id}/maintenance_planning` },
          { name: 'Plano de monitoramento/instrumentação', route: `/information_dam/${id}/monitoring_instrumentation_plan` },
          { name: 'Planejamento das inspeções de segurança da barragem', route: `/information_dam/${id}/dam_safety_inspections_planning` },
        ]
      }
    },
    {
      subMenu: {
        title: 'Registros',
        buttons: [
          { name: 'Relatórios de Inspeção de Segurança Regular (RISR)', route: `/information_dam/${id}/risr` },
          { name: 'Relatório Inspeção de Segurança Especial (RISE)', route: `/information_dam/${id}/rise` },
          { name: 'Registros de Operação', route: `/information_dam/${id}/operation_records` },
          { name: 'Registros de monitoramento geotécnico e instrumentação', route: `/information_dam/${id}/instrumentation_geotechnical_records` },
        ]
      }
    },
    {
      subMenu: {
        title: 'Revisão',
        buttons: [
          { name: 'Revisão Periódica de Segurança de Barragens (RPSB)', route: `/information_dam/${id}/rpsb` },
        ]
      }
    },
    {
      subMenu: {
        title: 'PAE',
        buttons: [
          { name: 'Plano de ação de emerência', route: `/information_dam/${id}/emergency_plan` },
          { name: 'Treinamento Interno Semestral', route: `/information_dam/${id}/semiannual_internal_training` },
          { name: 'Seminário Orientativo Anual', route: `/information_dam/${id}/annual_guidance_seminar` },
          { name: 'Simulado de Situação de Emergência', route: `/information_dam/${id}/emergency_situation_simulation` },
          { name: 'Registro de Situação de Emergências', route: `/information_dam/${id}/emergency_situation_register` },
        ]
      }
    },
  ];

  const handleButtonClick = (section: Sections) => {
    if (openSubmenu === section) {
      setOpenSubmenu(null);
    } else {
      setOpenSubmenu(section);
      setActiveSection(section);
    }
  };

  return (
    <>
      {smDown ? (
        <Box
          alignItems='center'
          display='flex'
          padding={1}
          justifyContent={'center'}
        >
          <MobileMenu menus={menus} />
        </Box>
      ) : (
        <>
          <Box
            display={'flex'}
            flexDirection={'row'}
            gap={2}
            alignItems={'center'}
            justifyContent={'center'}
            padding={1}
          >
            {sectionsData.map((sectionData) => (
              <React.Fragment key={sectionData.key}>
                <Button
                  variant={
                    (activeSection === sectionData.key || openSubmenu === sectionData.key)
                      ? 'contained'
                      : 'outlined'
                  }
                  fullWidth={smDown}
                  onClick={() => handleButtonClick(sectionData.key)}
                  size='small'
                  sx={{ borderRadius: '25px' }}
                >
                  <Typography variant='button' whiteSpace='nowrap' textOverflow='ellipsis' overflow='hidden'>
                    {sectionData.label}
                  </Typography>
                </Button>
                {smDown && openSubmenu === sectionData.key && (
                  <Box>
                    {sectionData.content}
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>
          {!smDown && (
            <Box
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'center'}
              alignContent={'center'}
            >
              {sectionsData.map((sectionData) => (
                <React.Fragment key={sectionData.key}>
                  {openSubmenu === sectionData.key && (
                    <Box
                      component={Paper}
                      variant='outlined'
                      padding={1}
                      width={'100%'}
                      sx={{ borderRadius: '25px' }}
                    >
                      {sectionData.content}
                    </Box>
                  )}
                </React.Fragment>
              ))}
            </Box>
          )}
        </>
      )}
    </>
  );
};